package com.essntl.uikit.components.file

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.essntl.uikit.components.iconbutton.flat.EsLargeFlatIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.image.AsyncImage
import com.essntl.uikit.components.image.AsyncUrlImage
import com.essntl.uikit.components.pdfviewer.PdfViewer
import com.essntl.uikit.components.pdfviewer.rememberPdfViewerState
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.mohamedrejeb.calf.core.ExperimentalCalfApi
import com.mohamedrejeb.calf.io.KmpFile

@OptIn(ExperimentalCalfApi::class)
@Composable
expect fun rememberFileSaverLauncher(
    onResult: (KmpFile?) -> Unit,
): FileSaverLauncher

@OptIn(ExperimentalCalfApi::class)
expect class FileSaverLauncher

@OptIn(ExperimentalCalfApi::class)
expect fun FileSaverLauncher.launch(
    bytes: ByteArray?,
    baseName: String,
    extension: String,
    initialDirectory: String?,
)

@OptIn(ExperimentalMaterial3Api::class, ExperimentalCalfApi::class)
@Composable
fun FileViewer(
    fileUrl: String,
    fileBaseName: String? = null,
    extension : String? = null,
    isDialogOpened: Boolean,
    onDismissRequest: () -> Unit,
    onDownloadSuccess: () -> Unit = {},
) {
    FileViewer(
        data = fileUrl,
        fileBaseName = fileBaseName,
        extension = extension,
        isDialogOpened = isDialogOpened,
        onDismissRequest = onDismissRequest,
        onDownloadSuccess = onDownloadSuccess,
    )
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalCalfApi::class)
@Composable
fun FileViewer(
    data: Any,
    fileBaseName: String? = null,
    extension : String? = null,
    isDialogOpened: Boolean,
    onDismissRequest: () -> Unit,
    onDownloadSuccess: () -> Unit = {},
) {
    if (isDialogOpened) {
        var pdfFileByteArray by remember { mutableStateOf<ByteArray?>(null) }
        var isPdfLoading by remember { mutableStateOf(true) }

        val isPdf = data is String && data.lowercase().endsWith("pdf")

        val fileSaverLauncher = rememberFileSaverLauncher(
            onResult = {
                if (it == null)
                    return@rememberFileSaverLauncher

                onDownloadSuccess()
            },
        )

        BasicAlertDialog(
            onDismissRequest = onDismissRequest,
            properties = DialogProperties(
                usePlatformDefaultWidth = false,
            ),
            modifier = Modifier
                .fillMaxSize(0.85f)
                .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                .background(MaterialTheme.esColorScheme.bgPrimary),
        ) {
            Box(
                modifier = Modifier.fillMaxSize(),
            ) {
                if (isPdf) {
                    val pdfViewerState = rememberPdfViewerState(url = data)

                    LaunchedEffect(pdfViewerState.byteArray) {
                        pdfFileByteArray = pdfViewerState.byteArray
                    }

                    LaunchedEffect(pdfViewerState.isLoading) {
                        isPdfLoading = pdfViewerState.isLoading
                    }

                    PdfViewer(
                        state = pdfViewerState,
                        modifier = Modifier.fillMaxSize(),
                    )
                } else {
                    AsyncImage(
                        data = data,
                        placeholder = {
                            Box(
                                contentAlignment = Alignment.Center,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(
                                        color = MaterialTheme.esColorScheme.bgPrimary,
                                        shape = RoundedCornerShape(20.dp),
                                    ),
                            ) {
                                EsSpinner(
                                    color = MaterialTheme.esColorScheme.iconPrimary,
                                    size = EsSpinnerSize.Large,
                                    modifier = Modifier
                                        .align(Alignment.Center),
                                )
                            }
                        },
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Fit,
                    )
                }

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(1.5f.spacing),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(20.dp),
                ) {
                    if (isPdf)
                        EsLargeFlatIconButton(
                            iconRes = EsIcons.Download,
                            onClick = {
                                // Open file saver
                                fileSaverLauncher.launch(
                                    bytes = pdfFileByteArray,
                                    baseName = fileBaseName ?: "file",
                                    extension = extension ?: "pdf",
                                    initialDirectory = null,
                                )
                            },
                            loading = isPdfLoading,
                            enabled = (pdfFileByteArray?.size ?: 0) > 0,
                        )

                    EsLargeFlatIconButton(
                        iconRes = EsIcons.Close,
                        onClick = onDismissRequest,
                    )
                }
            }
        }
    }
}